import getFetcher from 'api/utils/getFetcher';
import { useCallback, useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import type { LegacyUsageDataRecord } from 'types/legacyDashboard';

interface UsageDataResult {
  success: boolean;
  limit: number;
  size: number;
  continues: boolean;
  lastid: SessionId;
  data: Array<LegacyUsageDataRecord>;
}

export interface UsageDataError {}

interface UseUsageDataProps {
  organizationId?: OrgId;
  deviceId?: DeviceId;
  tagIds?: Array<TagId>;
  limit?: number;
  pageSize?: number;
}

const useUsageData = ({ organizationId, deviceId, tagIds, pageSize = 100 }: UseUsageDataProps) => {
  // https://swr.vercel.app/docs/pagination#example-2-cursor-or-offset-based-paginated-api
  const getKey = useCallback(
    (pageIndex: number, previousPageData: UsageDataResult | null) => {
      if (!organizationId) {
        return null;
      }

      // reached the end
      if (previousPageData && !previousPageData.lastid) {
        return null;
      }

      const params: Array<[string, string]> = [
        ['orgid', `${organizationId}`],
        ['limit', pageSize.toString()],
        ['getdeviceinfo', '1'],
      ];

      if (deviceId) {
        params.push(['deviceid', `${deviceId}`]);
      }

      if (tagIds) {
        tagIds.forEach((tagId) => {
          params.push(['tagids', `${tagId}`]);
        });
      }

      if (pageIndex !== 0 && previousPageData?.lastid) {
        params.push(['startafter', `${previousPageData.lastid}`]);
      }

      // add the cursor to the API endpoint
      return `${process.env.VITE_API_ROOT}/usage/data?${new URLSearchParams(params)}`;
    },
    [deviceId, organizationId, pageSize, tagIds]
  );

  const { data, setSize, size, error, isLoading, mutate, isValidating } = useSWRInfinite<
    UsageDataResult,
    UsageDataError
  >(getKey, getFetcher, {
    persistSize: true,
    dedupingInterval: 60000,
    focusThrottleInterval: 60000,
    refreshInterval: 60000,
  });

  const usageData = useMemo(
    () =>
      data
        ? data.reduce<Array<LegacyUsageDataRecord>>((acc, result) => {
            if (result && result.data) {
              return [...acc, ...result.data];
            }
            return acc;
          }, [])
        : [],
    [data]
  );

  // TODO: These were from the vercel docs as an example, but I find them a bit confusing. Consider renaming/rewriting/clarifying them. Or writing a wrapper around useSWRInfinite that makes them more clear.
  const isLoadingMore =
    isLoading || !!(size > 0 && data && (data[size - 1]?.data?.length ?? 0) === 0);
  const isEmpty = (data?.[0]?.data?.length ?? 0) === 0;
  const end = isEmpty || !!(data && (data[data.length - 1]?.data?.length ?? 0) < pageSize);
  const isRefreshing = isValidating && !!data && data.length === size;

  const loadMore = useCallback(() => {
    setSize((lastSize) => lastSize + 1);
  }, [setSize]);

  return {
    usageData,
    end,
    loadMore,
    isEmpty,
    error,
    isLoading,
    isLoadingMore,
    mutate,
    isValidating,
    isRefreshing,
  };
};

export default useUsageData;
